/**
 * Canonical <-> room space, SPEC-131.
 *
 * A diagram's node positions are stored in the diagram's own canonical space; a room fits that
 * arrangement to its own floor when it opens the diagram. Today there is exactly one room
 * template — 8x8m of floor, 3m of ceiling (`Tunet.Core/Data/DbSeeder.cs`, `BasicRoomFloorplan`)
 * — and A1 carried every node position across byte-for-byte when `Diagram` was extracted from
 * `Room`, so canonical space and that one template's own extent are numerically the same thing
 * (plan/diagram-extraction.md, "What A1 actually cost"). `CANONICAL_FLOORPLAN` below is that
 * extent, made explicit — the reference frame every stored position is authored against until a
 * second template exists and a diagram can record which one it started life in.
 *
 * Pure numbers in, pure numbers out — no Scene, no Mesh — the same split `GridSnap.ts` already
 * uses so this is testable without a headless renderer, and so `GridSnap` itself never has to
 * know this exists: grid snapping stays entirely in canonical space (CLAUDE.md's own instruction
 * for this task), and it is *callers* of both modules — `PossessionManager`, `PlacementManager`
 * — that cross the boundary, converting once on the way in and once on the way out.
 */

// spec: SPEC-131

export interface Point3 {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/** A room's floor extent — the fields of `RoomFloorplanDto` this module actually needs, kept as
 *  its own narrow shape so this file has no dependency on the generated contracts module. */
export interface FloorExtent {
  readonly width: number;
  readonly depth: number;
  readonly wallHeight: number;
}

/**
 * The extent every node position is authored against — see this module's own doc comment for
 * why this is a fixed constant rather than something read off a `Diagram` row: nothing stores it
 * today, and this is the one template that has ever existed to author against.
 */
export const CANONICAL_FLOORPLAN: FloorExtent = { width: 8, depth: 8, wallHeight: 3 };

export interface RoomFit {
  /** Canonical -> this room's own floor. What a render reads. */
  toRoom(position: Point3): Point3;

  /** This room's own floor -> canonical. What a write sends. */
  toCanonical(position: Point3): Point3;
}

/**
 * No scaling at all — every room until a second template exists, and every test that does not
 * care about this task's own subject. `RoomView`'s own default before `setRoomFit` is ever
 * called, so nothing between construction and the first snapshot has to null-check a fit that
 * has not arrived yet.
 */
export const IDENTITY_ROOM_FIT: RoomFit = {
  toRoom: (position) => position,
  toCanonical: (position) => position,
};

/**
 * A per-axis scale, anchored at the origin — both spaces put the floor's own centre at
 * `x = 0, z = 0` and the floor itself at `y = 0` (`SceneRoot.buildRoom`'s own wall placement at
 * `±halfWidth`/`±halfDepth`), so a plain scale needs no translation to line the two up. Height
 * scales the same way, off the room's own wall height, rather than being left unscaled — a node
 * placed near the canonical ceiling should read as "near the ceiling" in a room of any height,
 * not drift into (or through) the walls of a shorter one.
 *
 * `safeRatio` guards a zero-sized dimension (an unset or malformed floorplan) by falling back to
 * an identity scale on that axis rather than dividing by zero into `Infinity`/`NaN` — a template
 * this degenerate is a data bug elsewhere, and the fit should not compound it into a node that
 * can never be found again.
 */
export function computeRoomFit(room: FloorExtent, canonical: FloorExtent = CANONICAL_FLOORPLAN): RoomFit {
  const scaleX = safeRatio(room.width, canonical.width);
  const scaleY = safeRatio(room.wallHeight, canonical.wallHeight);
  const scaleZ = safeRatio(room.depth, canonical.depth);

  return {
    toRoom: (position) => ({
      x: position.x * scaleX,
      y: position.y * scaleY,
      z: position.z * scaleZ,
    }),
    toCanonical: (position) => ({
      x: position.x / scaleX,
      y: position.y / scaleY,
      z: position.z / scaleZ,
    }),
  };
}

function safeRatio(roomExtent: number, canonicalExtent: number): number {
  return canonicalExtent === 0 ? 1 : roomExtent / canonicalExtent;
}
